import { useEffect, useLayoutEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Layout } from "./components/Layout";
import { HomePage } from "./components/HomePage";
import {
  AboutPage,
  AdminPage,
  AreasPage,
  BlogPage,
  BlogPostPage,
  ContactPage,
  FaqPage,
  GalleryPage,
  LegalPage,
  NotFoundPage,
  QuotePage,
  ReviewsPage,
  ServicePage,
  ServicesPage,
  TrackPage,
} from "./components/Pages";

const titles: Record<string, string> = {
  "/": "Horncastle Flooring | Flooring fitted properly",
  "/services": "Flooring Supply & Fitting | Horncastle Flooring",
  "/gallery": "Our Flooring Work | Horncastle Flooring",
  "/about": "About Horncastle Flooring Retailer",
  "/reviews": "Customer Reviews | Horncastle Flooring",
  "/blog": "Flooring Advice | Horncastle Flooring",
  "/get-a-quote": "Book a Free Measure & Quote | Horncastle Flooring",
  "/track": "Track Your Flooring Job | Horncastle Flooring",
  "/faq": "Flooring FAQs | Horncastle Flooring",
  "/contact": "Contact & Showroom | Horncastle Flooring",
  "/admin": "Admin preview | Horncastle Flooring",
};

function Seo() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = titles[pathname] ?? (pathname.startsWith("/services/") ? "Flooring in Lincolnshire | Horncastle Flooring" : pathname.startsWith("/areas/") ? "Local Flooring Fitters | Horncastle Flooring" : "Horncastle Flooring Retailer");
    document.querySelector('link[rel="canonical"]')?.remove();
    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = `https://horncastle-flooring.pages.dev${pathname}`;
    document.head.appendChild(canonical);
  }, [pathname]);
  return null;
}

function StructuredData() {
  const data = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    name: "Horncastle Flooring Retailer",
    image: "https://horncastle-flooring.pages.dev/images/gallery/storefront.jpg",
    telephone: "+441507237890",
    email: "horncastleflooringretailer@mail.com",
    address: { "@type": "PostalAddress", streetAddress: "Eggbert Court, Wharf Road", addressLocality: "Horncastle", addressRegion: "Lincolnshire", postalCode: "LN9 5FQ", addressCountry: "GB" },
    areaServed: ["Horncastle", "Louth", "Woodhall Spa", "Skegness", "Lincoln", "Spilsby", "Wragby"],
    priceRange: "££",
    sameAs: [],
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />;
}

function RouteMotion() {
  const reduceMotion = useReducedMotion();

  useLayoutEffect(() => {
    if (reduceMotion) return;
    gsap.registerPlugin(ScrollTrigger);
    const frame = window.requestAnimationFrame(() => {
      const root = document.querySelector<HTMLElement>(".route-stage");
      if (!root) return;
      const selectors = [
        ".section h2:not(.text-reveal)",
        ".section h3:not(.text-reveal)",
        ".service-list-copy > *",
        ".answer-grid > *",
        ".story-grid > *",
        ".facts-section dl > div",
        ".review-card",
        ".review-summary > *",
        ".post-grid article",
        ".faq-full details",
        ".form-aside > *",
        ".quote-form > *",
        ".narrow-form > *",
        ".contact-details > *",
        ".contact-form > *",
        ".article-body > *",
        ".legal-page section",
        ".area-links > *",
      ];
      const targets = gsap.utils.toArray<HTMLElement>(selectors.join(","), root)
        .filter((element) => !element.closest("[data-reveal]") && !element.dataset.motionBound);
      targets.forEach((element) => { element.dataset.motionBound = "true"; });
      gsap.set(targets, { opacity: 0, y: 34 });
      ScrollTrigger.batch(targets, {
        start: "top 91%",
        once: true,
        onEnter: (batch) => gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: .82,
          stagger: .065,
          ease: "power3.out",
          overwrite: true,
        }),
      });
      ScrollTrigger.refresh();
    });
    return () => {
      window.cancelAnimationFrame(frame);
      ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger.vars.trigger instanceof Element && trigger.vars.trigger.closest(".route-stage")) trigger.kill();
      });
    };
  }, [reduceMotion]);

  return null;
}

export default function App() {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  return (
    <Layout>
      <Seo />
      <StructuredData />
      <motion.div
        className="route-stage"
        key={location.pathname}
        initial={reduceMotion ? false : { opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: reduceMotion ? 0 : .38, ease: [0.16, 1, 0.3, 1] }}
      >
        <RouteMotion />
        <Routes location={location}>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:slug" element={<ServicePage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/areas/:town" element={<AreasPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/get-a-quote" element={<QuotePage />} />
          <Route path="/track" element={<TrackPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/privacy-policy" element={<LegalPage type="privacy-policy" />} />
          <Route path="/cookie-policy" element={<LegalPage type="cookie-policy" />} />
          <Route path="/terms-conditions" element={<LegalPage type="terms-conditions" />} />
          <Route path="/refund-policy" element={<LegalPage type="refund-policy" />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </motion.div>
    </Layout>
  );
}
